import { useEffect, useState } from "react";
import { Box, IconButton, Link } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import type { PortalNavItem, SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, portalFont } from "../partnerTheme";

export default function PartnerPortalHeader({ campaign }: { campaign: SponsorCampaign }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const showPartnerMark =
    Boolean(campaign.sponsorLogo) &&
    campaign.sponsorLogo !== campaign.comedyuoLogo &&
    !/comedyuo/i.test(campaign.sponsorName);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        bgcolor: scrolled ? "rgba(10,10,10,0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? "1px solid var(--portal-border)" : "1px solid transparent",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          py: { xs: 1.5, md: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 1.25, md: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.25, md: 1.75 },
            minWidth: 0,
            flex: "1 1 auto",
            overflow: "hidden",
          }}
        >
          {showPartnerMark ? (
            <>
              <Box
                component="img"
                src={campaign.sponsorLogo}
                alt={`${campaign.sponsorName} logo`}
                sx={{
                  height: { xs: 26, sm: 32, md: 40 },
                  width: "auto",
                  maxWidth: { xs: "46vw", sm: 200, md: 240 },
                  objectFit: "contain",
                  flexShrink: 1,
                }}
              />
              <Box
                aria-hidden
                sx={{
                  width: "1px",
                  height: { xs: 18, md: 28 },
                  bgcolor: "rgba(255,255,255,0.85)",
                  flexShrink: 0,
                  alignSelf: "center",
                }}
              />
            </>
          ) : null}
          <Box
            component="img"
            src={campaign.comedyuoLogo}
            alt="ComedyUO"
            sx={{
              height: { xs: 40, sm: 52, md: 64 },
              width: "auto",
              maxWidth: { xs: "52vw", sm: 220, md: 280 },
              objectFit: "contain",
              filter: "brightness(0) invert(1)",
              flexShrink: 0,
            }}
          />
        </Box>

        <Box
          component="nav"
          aria-label="Campaign sections"
          sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2.5 }}
        >
          {campaign.nav.map((item) => (
            <NavAnchor key={item.id} item={item} />
          ))}
        </Box>

        <IconButton
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            color: "var(--portal-text)",
            flexShrink: 0,
          }}
        >
          {open ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      {open ? (
        <Box
          component="nav"
          aria-label="Campaign sections mobile"
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            gap: 0.5,
            px: 2,
            pb: 2,
            borderTop: "1px solid var(--portal-border)",
            bgcolor: "rgba(10,10,10,0.96)",
          }}
        >
          {campaign.nav.map((item) => (
            <Link
              key={item.id}
              href={item.href}
              onClick={() => setOpen(false)}
              sx={{
                fontFamily: portalFont,
                color: "var(--portal-text)",
                textDecoration: "none",
                py: 1.25,
                px: 1,
                borderRadius: "10px",
                fontWeight: 600,
                fontSize: "0.9375rem",
                "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
                "&:focus-visible": {
                  outline: "2px solid var(--portal-accent)",
                  outlineOffset: 2,
                },
              }}
            >
              {item.label}
            </Link>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function NavAnchor({ item }: { item: PortalNavItem }) {
  return (
    <Link
      href={item.href}
      sx={{
        fontFamily: portalFont,
        color: "var(--portal-text-muted)",
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: 500,
        transition: "color 0.15s ease",
        "&:hover": { color: "var(--portal-text)" },
        "&:focus-visible": {
          outline: "2px solid var(--portal-accent)",
          outlineOffset: 3,
          borderRadius: 2,
        },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      {item.label}
    </Link>
  );
}
